import type { VisibilityTier } from '@/shared/types/projectData';

const VISIBILITY_ORDER: Record<VisibilityTier, number> = {
  internal: 0,
  partner: 1,
  client: 2,
};

const ORDERED_VISIBILITY_TIERS: VisibilityTier[] = ['internal', 'partner', 'client'];

export interface VisibilityActionOption {
  value: VisibilityTier;
  mode: 'direct' | 'request';
}

export const normalizeVisibilityTier = (value?: string | null): VisibilityTier => {
  if (value === 'partner' || value === 'client') {
    return value;
  }
  return 'internal';
};

export const canManageVisibilityRewrites = (role?: string | null): boolean => role === 'admin';

export const getAllowedVisibilityTiers = ({
  role,
  currentVisibilityTier,
  isCreate = false,
}: {
  role?: string | null;
  currentVisibilityTier?: string | null;
  isCreate?: boolean;
}): VisibilityTier[] => {
  if (role === 'admin') {
    return ORDERED_VISIBILITY_TIERS;
  }

  if (role !== 'employee') {
    return [];
  }

  if (isCreate) {
    return ['internal'];
  }

  const normalizedCurrentTier = normalizeVisibilityTier(currentVisibilityTier);
  const maxAllowedOrder = VISIBILITY_ORDER[normalizedCurrentTier];
  return ORDERED_VISIBILITY_TIERS.filter((tier) => VISIBILITY_ORDER[tier] <= maxAllowedOrder);
};

export const getRequestableVisibilityTiers = ({
  role,
  currentVisibilityTier,
  isCreate = false,
}: {
  role?: string | null;
  currentVisibilityTier?: string | null;
  isCreate?: boolean;
}): VisibilityTier[] => {
  if (role !== 'employee' || isCreate) {
    return [];
  }

  const normalizedCurrentTier = normalizeVisibilityTier(currentVisibilityTier);
  return ORDERED_VISIBILITY_TIERS.filter((tier) => VISIBILITY_ORDER[tier] > VISIBILITY_ORDER[normalizedCurrentTier]);
};

export const getVisibilityActionOptions = ({
  role,
  currentVisibilityTier,
  isCreate = false,
}: {
  role?: string | null;
  currentVisibilityTier?: string | null;
  isCreate?: boolean;
}): VisibilityActionOption[] => {
  const direct = getAllowedVisibilityTiers({ role, currentVisibilityTier, isCreate }).map((value) => ({
    value,
    mode: 'direct' as const,
  }));
  const requestable = getRequestableVisibilityTiers({ role, currentVisibilityTier, isCreate }).map((value) => ({
    value,
    mode: 'request' as const,
  }));
  return [...direct, ...requestable];
};

export const getMostRestrictiveVisibilityTier = (
  tiers: Iterable<string | null | undefined>,
): VisibilityTier => {
  let mostRestrictive: VisibilityTier = 'client';
  for (const tier of tiers) {
    const normalizedTier = normalizeVisibilityTier(tier);
    if (VISIBILITY_ORDER[normalizedTier] < VISIBILITY_ORDER[mostRestrictive]) {
      mostRestrictive = normalizedTier;
    }
  }
  return mostRestrictive;
};
